import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, inject, OnInit, resource, signal } from '@angular/core';
import { rxResource, takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { combineLatest, debounceTime, delay, distinctUntilChanged, filter, firstValueFrom, map, of, skip, startWith } from 'rxjs';
import { UrlNormalizerDirective } from '../../../directives/url-normalizer.directive';
import { Address } from '../../../models/address';
import { MemberType, OngoingRelationship, SupplierFeedback, ThreeLevelScale } from '../../../models/enums';
import { Initiative } from '../../../models/initiative';
import { Organisation } from '../../../models/organisation';
import { LastUpdatePipe } from '../../../pipes/last-update.pipe';
import { InitiativeService } from '../../../services/initiative.service';
import { LanguageService } from '../../../services/language.service';
import { OrganisationService, OrganisationSimilarityCheckPayload } from '../../../services/organisation.service';
import { ProjectService } from '../../../services/project.service';
import { ReferenceDataService } from '../../../services/reference-data.service';
import { isPastDate, MY_DATE_FORMATS, normalizeDate, normalizeDateTime, urlValidator } from '../../../utils/utils';
import { AdvancedChipSelectComponent } from '../../misc/advanced-chip-select/advanced-chip-select.component';
import { separatorKeysCodes } from '../../misc/nested-options';
import { createPostalAddressFormGroup, PostalAddressComponent, PostalAddressForm } from '../../misc/postal-address/postal-address.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DateTime } from 'luxon';
import { Audience } from '../../../models/audience';
import { MembershipRecord } from '../../../models/membership-record';
import { PhoneNumber } from '../../../models/phone-number';
import { Product } from '../../../models/product';
import { Project } from '../../../models/project';
import { SimilarityList } from '../../../models/similarity-list';
import { StakeholderGroup } from '../../../models/stakeholder-group';
import { TopicOfInterest } from '../../../models/topic-of-interest';
import { SimilarityListPipe } from '../../../pipes/similarity-list.pipe';
import { ContactService } from "../../../services/contact.service";
import { O365AuthService } from '../../../services/o365-auth.service';
import { HistoryPanelComponent } from '../../misc/history-panel/history-panel.component';
import { createPhoneNumberFormGroup, PhoneNumberComponent, PhoneNumberForm } from '../../misc/phone-number/phone-number.component';
import { ContactSummary } from '../../../models/contact-summary';
import { Language } from '../../../models/language';
import { SearchableSelectComponent, SelectOption } from '../../misc/searchable-select/searchable-select.component';
import { UrlMakerPipe } from '../../../pipes/url-maker.pipe';

/* Types */
export type MembershipRecordFormGroup = FormGroup<MembershipRecordForm>;
export type PostalAddressFormGroup = FormGroup<PostalAddressForm>;
export type PhoneNumberFormGroup = FormGroup<PhoneNumberForm>;

/* Interfaces */
export interface MembershipRecordForm {
    id: FormControl<number | null>;
    trialMember: FormControl<boolean>;
    memberType: FormControl<MemberType | null>;
    topics: FormControl<TopicOfInterest[]>;
    contractStartDate: FormControl<string | null>;  // ISO date string
    contractEndDate: FormControl<string | null>;  // ISO date string
    language: FormControl<SelectOption | null>;
    engagementLevel: FormControl<ThreeLevelScale | null>;
    notes: FormControl<string>;
    primaryContact: FormControl<SelectOption | null>;
    invoicingContact: FormControl<SelectOption | null>;
    contractContact: FormControl<SelectOption | null>;
}

export interface SupplierDetailsForm {
    id: FormControl<number | null>;
    taxId: FormControl<string | null>;
    supplierEmail: FormControl<string | null>;
    products: FormControl<Product[]>;
    additionalInfo: FormControl<string | null>;
    ongoingRelationship: FormControl<OngoingRelationship | null>;
    supplierFeedback: FormControl<SupplierFeedback | null>;
    website: FormControl<string | null>;
    driveFolder: FormControl<string | null>;
}

export interface OrganisationForm {
    id: FormControl<number | null>;
    name: FormControl<string | null>;
    acronym: FormControl<string | null>;
    stakeholderGroups: FormControl<StakeholderGroup[]>;
    addresses: FormArray<PostalAddressFormGroup>;
    taxId: FormControl<string | null>;
    phoneNumbers: FormArray<PhoneNumberFormGroup>;
    projects: FormControl<Project[]>;
    initiatives: FormControl<Initiative[]>;
    topics: FormControl<TopicOfInterest[]>;
    website: FormControl<string | null>;
    targetAudiences: FormControl<Audience[]>;
    additionalInfo: FormControl<string | null>;
    archived: FormControl<boolean>;
    supplierDetails: FormGroup<SupplierDetailsForm>;
    membershipRecords: FormArray<FormGroup<MembershipRecordForm>>;
}

/* Validators */
export const membershipRecordValidator: ValidatorFn = (control) => {
    // console.debug('membershipRecordValidator');
    const fg = control as MembershipRecordFormGroup;
    const start = normalizeDateTime(fg.controls.contractStartDate.value);
    const end = normalizeDateTime(fg.controls.contractEndDate.value);
    // console.debug('membershipRecordValidator', start?.toISODate(), end?.toISODate());

    if (start && end && start > end) {
        console.error('invalid dates', start.toISODate(), end.toISODate());
        const existing = fg.controls.contractEndDate.errors ?? {};
        fg.controls.contractEndDate.setErrors({
            ...existing,
            invalidDateRange: true,
        });
        return { invalidDateRange: true };
    }
    return null;
};

export function membershipRecordListValidatorFactory(component: EditOrganisationComponent): ValidatorFn {
    return (control: AbstractControl) => {
        // console.debug('membershipRecordListValidator');
        const formArray = control as FormArray<MembershipRecordFormGroup>;

        const intervals: { start: DateTime | null; end: DateTime | null }[] = [
            ...formArray.controls.map(value => ({
                start: normalizeDateTime(value.controls['contractStartDate'].value),
                end: normalizeDateTime(value.controls['contractEndDate'].value)
            })),
            ...component.pastMembershipRecords.map(mr => ({
                start: normalizeDateTime(mr.contractStartDate),
                end: normalizeDateTime(mr.contractEndDate)
            })),

        ];

        // it can be that start is null in case a new MembershipRecord is created
        const validIntervals = intervals
            .filter(i => i.start !== null)
            .map(i => ({
                start: i.start!.startOf('day'),
                end: i.end ? i.end.startOf('day') : null
            }))
            .sort((a, b) => a.start.toMillis() - b.start.toMillis());

        // console.table(
        //     validIntervals.map(i => ({
        //         start: i.start?.toISODate(),
        //         end: i.end ? i.end.toISODate() : '(open)'
        //     }))
        // );

        // Check for overlapping intervals (knowing that they are sorted on start date)
        for (let i = 0; i < validIntervals.length - 1; i++) {
            const current = validIntervals[i];
            const next = validIntervals[i + 1];

            // if current has no end → it’s open and overlaps with any later start
            // Only the last record can have no end
            if (current.end === null) {
                return { overlappingPeriods: true };
            }

            // date‑only comparison
            if (current.end > next.start) {
                return { overlappingPeriods: true };
            }
        }
        return null;
    };
}


@Component({
    selector: 'app-edit-organisation',
    imports: [
        AdvancedChipSelectComponent,
        CommonModule,
        FormsModule,
        LastUpdatePipe,
        MatAutocompleteModule,
        MatButtonModule,
        MatCardModule,
        MatCheckboxModule,
        MatDatepickerModule,
        MatExpansionModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatListModule,
        MatRadioModule,
        MatSelectModule,
        MatTabsModule,
        MatTooltipModule,
        PostalAddressComponent,
        ReactiveFormsModule,
        MatSliderModule,
        UrlNormalizerDirective,
        MatButtonToggleModule,
        PhoneNumberComponent,
        MatProgressSpinnerModule,
        SimilarityListPipe,
        HistoryPanelComponent,
        SearchableSelectComponent,
        UrlMakerPipe
    ],
    providers: [
        provideLuxonDateAdapter(),
        { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
        { provide: MAT_DATE_LOCALE, useValue: 'fr' },
    ],
    templateUrl: './edit-organisation.component.html',
    styleUrl: './edit-organisation.component.scss'
})
export class EditOrganisationComponent implements OnInit {
    SupplierFeedback = SupplierFeedback;
    OngoingRelationship = OngoingRelationship;
    ThreeLevelScale = ThreeLevelScale;
    MemberType = MemberType;
    Validators = Validators;
    isPastDate = isPastDate;

    readonly additionalInfoMaxLen = 500;
    readonly _snackBar = inject(MatSnackBar);
    readonly separatorKeysCodes = separatorKeysCodes;

    activeMembershipTab = signal(0);


    organisationFG!: FormGroup<OrganisationForm>;
    isSupplierControl!: FormControl<boolean>;

    returnUrl: string = '/organisations';

    mode: string = 'Create'; // Create, Update or Display
    organisation: Organisation = new Organisation();
    resetCounter = 0; // used to reset the filter form
    isSupplier = signal(false);
    isMember = signal(false);

    private destroyRef = inject(DestroyRef);
    readonly refDataService = inject(ReferenceDataService);
    readonly organisationService = inject(OrganisationService);
    readonly projectService = inject(ProjectService);
    readonly initiativeService = inject(InitiativeService);
    readonly languageService = inject(LanguageService);
    readonly contactService = inject(ContactService);
    readonly authService = inject(O365AuthService);

    readonly allProjects = toSignal(this.projectService.getAllProjects(), { initialValue: [] });
    readonly allProjectOptions = computed(() => this.allProjects());
    readonly allAudiences = this.refDataService.getAudiences();
    readonly allAudienceOptions = computed(() => this.allAudiences());
    readonly allProducts = this.refDataService.getProducts();
    readonly allProductOptions = computed(() => this.allProducts());
    readonly allGeneralTopics = this.refDataService.getGeneralTopicsOfInterest();
    readonly allGeneralTopicOptions = computed(() => this.allGeneralTopics());
    readonly allStakeholderGroups = this.refDataService.getOrgStakeholderGroups();
    readonly allStakeholderGroupOptions = computed(() => this.allStakeholderGroups());

    readonly initiatives = resource<Initiative[], unknown>({
        loader: () => firstValueFrom(this.initiativeService.getAllInitiatives()),
        defaultValue: []
    });
    readonly allInitiativeOptions = computed(() => this.initiatives.value())
    readonly allMemberTopics = this.refDataService.getMemberTopicsOfInterest();
    readonly allMemberTopicOptions = computed(() => this.allMemberTopics());

    readonly contacts = resource<ContactSummary[], unknown>({
        loader: () => firstValueFrom(this.contactService.getAllContactsSummary()),
        defaultValue: []
    })
    readonly allContactOptions = computed(() => this.contacts.value().map(v => (
        {
            id: v.id,
            name: v.lastName + ', ' + v.firstName,
            icons: [
                ...(v.archived ? ['archive'] : []),
            ]
        })));

    readonly languages = resource<Language[], unknown>({
        loader: () => firstValueFrom(this.languageService.getAllLanguages()),
        defaultValue: []
    });
    readonly allLanguageOptions = computed(() => this.languages.value().map(v => ({ id: v.id, name: v.name, icons: [] })))

    private checkDuplicateParams = signal<OrganisationSimilarityCheckPayload | undefined>(undefined);

    duplicateEntryResource = rxResource<SimilarityList<any> | null, OrganisationSimilarityCheckPayload | undefined>({
        params: this.checkDuplicateParams,
        stream: ({ params }) => {
            if (!params) {
                return of(null);
            }
            return this.organisationService.checkForSimilarOrganisations(params).pipe(delay(250));;
        },
    });

    constructor(
        private readonly fb: FormBuilder, private readonly router: Router,
        private readonly route: ActivatedRoute, private readonly dialog: MatDialog) { }

    ngOnInit(): void {
        this.createForm();
        this.returnUrl =
            this.route.snapshot.queryParamMap.get('returnUrl') ?? '/organisations';
        const idParam = this.route.snapshot.paramMap.get('id');
        if (idParam === null) {
            this.mode = 'Create';
            this.isSupplier.set(false);
            this.supplierDetailsFG.disable();
        } else {
            const organisationId = Number(idParam);
            if (!idParam || Number.isNaN(organisationId)) {
                this.router.navigateByUrl(this.returnUrl, {
                    state: { message: '❌ No such organisation: ' + idParam }
                });
                return;
            }
            this.mode = 'Update';
            this.organisationService.getOrganisationById(organisationId).subscribe({
                next: (org: Organisation) => {
                    this.organisation = org;
                    console.debug('organisation', this.organisation);
                    this.organisationFG.patchValue({
                        id: this.organisation.id,
                        name: this.organisation.name,
                        acronym: this.organisation.acronym,
                        additionalInfo: this.organisation.additionalInfo,
                        taxId: this.organisation.taxId,
                        website: this.organisation.website,
                        archived: this.organisation.archived,
                        stakeholderGroups: this.organisation.stakeholderGroups,
                        initiatives: this.organisation.initiatives,
                        topics: this.organisation.topics,
                        targetAudiences: this.organisation.targetAudiences,
                        projects: this.organisation.projects
                    });

                    this.addressesFA.clear();
                    if (org.addresses.length > 0) {
                        org.addresses.forEach(item => {
                            this.addAddress(item);
                        })
                    } else {
                        this.addAddress();
                    }
                    this.phoneNumbersFA.clear();
                    org.phoneNumbers.forEach(item => {
                        this.addPhoneNumber(item);
                    })

                    if (org.supplierDetails) {
                        this.isSupplier.set(true);
                        this.supplierDetailsFG.patchValue(org.supplierDetails ?? {});
                        this.supplierDetailsFG.enable();
                        this.organisationFG.controls.stakeholderGroups.setValidators([]);
                        this.organisationFG.controls.stakeholderGroups.updateValueAndValidity();
                    } else {
                        this.isSupplier.set(false);
                        this.supplierDetailsFG.disable();
                        this.organisationFG.controls.stakeholderGroups.setValidators(Validators.required);
                        this.organisationFG.controls.stakeholderGroups.updateValueAndValidity();
                    }

                    this.currentMembershipRecords.forEach(record => {
                        this.membershipRecordsFA.push(
                            this.createMembershipRecordFormGroup({ ...record })
                        );
                    });
                },
                error: (err: string) => {
                    console.error('Failed to load organisation:', err);
                    this.router.navigateByUrl(this.returnUrl,
                        {
                            state: { message: '❌ Failed to load organisation: ' + organisationId }
                        }
                    );
                }
            })
        }

        this.membershipRecordsFA.statusChanges.subscribe(status => {
            if (status === 'INVALID') {
                const errors = this.membershipRecordsFA.errors;
                if (errors?.['overlappingPeriods']) {
                    this._snackBar.open('❌ There are overlapping membership records', 'Close', {
                        duration: 3000
                    });
                }
            }
        });

    }

    private createSupplierDetailsFormGroup(initial?: Partial<SupplierDetailsForm>): FormGroup<SupplierDetailsForm> {
        return this.fb.group<SupplierDetailsForm>({
            id: this.fb.control(initial?.id ?? null),
            supplierEmail: this.fb.control(initial?.supplierEmail ?? '', { validators: Validators.email }),
            taxId: this.fb.control(initial?.taxId ?? '', { nonNullable: false }),
            products: this.fb.control([], { nonNullable: true }),
            additionalInfo: this.fb.control(initial?.additionalInfo ?? '', { nonNullable: true }),
            ongoingRelationship: this.fb.control(initial?.ongoingRelationship ?? OngoingRelationship.NOT_YET, { nonNullable: true }),
            supplierFeedback: this.fb.control(initial?.supplierFeedback ?? SupplierFeedback.NOT_YET, { nonNullable: true }),
            driveFolder: this.fb.control(initial?.driveFolder ?? '', { nonNullable: true }),
            website: this.fb.control(initial?.website ?? '', { validators: urlValidator() })
        });
    }

    private createMembershipRecordFormGroup(record?: Partial<MembershipRecord>): FormGroup<MembershipRecordForm> {
        return this.fb.group<MembershipRecordForm>({
            id: this.fb.control(record?.id ?? null),
            trialMember: this.fb.control(record?.trialMember ?? false, { nonNullable: true }),
            memberType: this.fb.control(record?.memberType ?? MemberType.FULL),
            topics: this.fb.control(record?.topics ?? [], { nonNullable: true }),
            contractStartDate: this.fb.control(record?.contractStartDate ?? null,
                { validators: Validators.required, updateOn: 'blur' }),
            contractEndDate: this.fb.control(record?.contractEndDate ?? null, { updateOn: 'blur' }),
            language: this.fb.control((record?.language ? {
                id: record.language.id,
                name: record.language.name,
                icons: []
            } : null)),
            engagementLevel: this.fb.control(record?.engagementLevel ?? ThreeLevelScale.MEDIUM),
            notes: this.fb.control(record?.notes ?? '', { nonNullable: true }),
            primaryContact: this.fb.control((record?.primaryContact ? {
                id: record.primaryContact.id,
                name: record.primaryContact.lastName + ', ' + record.primaryContact.firstName,
                icons: [
                    ...(record.primaryContact.archived ? ['archive'] : []),
                ]
            } : null)),
            invoicingContact: this.fb.control((record?.invoicingContact ? {
                id: record.invoicingContact.id,
                name: record.invoicingContact.lastName + ', ' + record.invoicingContact.firstName,
                icons: [
                    ...(record.invoicingContact.archived ? ['archive'] : []),
                ]
            } : null)),
            contractContact: this.fb.control((record?.contractContact ? {
                id: record.contractContact.id,
                name: record.contractContact.lastName + ', ' + record.contractContact.firstName,
                icons: [
                    ...(record.contractContact.archived ? ['archive'] : []),
                ]
            } : null)),
        }, {
            validators: [membershipRecordValidator]
        });
    }

    private createForm(): void {
        this.organisationFG = this.fb.group<OrganisationForm>({
            id: this.fb.control(this.organisation?.id ?? null),
            name: this.fb.control(this.organisation?.name ?? '', { validators: Validators.required }),
            archived: this.fb.control(this.organisation?.archived ?? false, { nonNullable: true }),
            acronym: this.fb.control(this.organisation?.acronym ?? '', { validators: Validators.required }),
            stakeholderGroups: this.fb.control([], { nonNullable: true, validators: Validators.required }),
            addresses: this.fb.array<FormGroup<PostalAddressForm>>([createPostalAddressFormGroup(false, true)]),
            taxId: this.fb.control(this.organisation?.taxId ?? '', { nonNullable: true }),
            phoneNumbers: this.fb.array<FormGroup<PhoneNumberForm>>([]),
            projects: this.fb.control([], { nonNullable: true }),
            initiatives: this.fb.control([], { nonNullable: true }),
            topics: this.fb.control([], { nonNullable: true }),
            website: this.fb.control(this.organisation?.website ?? '', { validators: urlValidator() }),
            targetAudiences: this.fb.control([], { nonNullable: true }),
            additionalInfo: this.fb.control(this.organisation?.additionalInfo ?? '', { validators: Validators.maxLength(this.additionalInfoMaxLen) }),
            supplierDetails: this.createSupplierDetailsFormGroup(),
            membershipRecords: this.fb.array<FormGroup<MembershipRecordForm>>([], { validators: [membershipRecordListValidatorFactory(this)] })
        })
        this.isSupplierControl = this.fb.control(!!this.organisation?.supplierDetails, { nonNullable: true });

        combineLatest([
            this.organisationFG.get('name')!.valueChanges.pipe(
                startWith('')),
            this.organisationFG.get('acronym')!.valueChanges.pipe(
                startWith('')),
        ]).pipe(
            skip(1),
            map(([name, acronym]) => ({
                id: this.organisation.id,
                name: (name ?? '').trim(),
                acronym: (acronym ?? '').trim()
            }) as OrganisationSimilarityCheckPayload),
            filter(payload => payload.name !== '' || payload.acronym !== ''),
            debounceTime(300),
            distinctUntilChanged((prev, curr) =>
                prev.name === curr.name && prev.acronym === curr.acronym
            ),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(payload => {
            this.checkDuplicateParams.set(payload);
        });
    }

    get pastMembershipRecords(): MembershipRecord[] {
        return this.organisation.membershipRecords.filter((mr: MembershipRecord) =>
            (mr.contractEndDate && DateTime.fromISO(mr.contractEndDate) < DateTime.now()));
    }

    get currentMembershipRecords(): MembershipRecord[] {
        return this.organisation.membershipRecords.filter((mr: MembershipRecord) =>
            (!mr.contractEndDate || DateTime.fromISO(mr.contractEndDate) >= DateTime.now()));
    }

    get supplierDetailsFG(): FormGroup<SupplierDetailsForm> {
        return this.organisationFG.controls.supplierDetails
    }

    get membershipRecordsFA(): FormArray<MembershipRecordFormGroup> {
        return this.organisationFG.controls.membershipRecords;
    }

    get addressesFA() {
        return this.organisationFG.controls.addresses;
    }

    get phoneNumbersFA() {
        return this.organisationFG.controls.phoneNumbers;
    }

    get isCurrentMember() {
        const today = DateTime.now();
        const hasActive = this.organisation.membershipRecords.some(r => {
            const start = r.contractStartDate
                ? DateTime.fromISO(r.contractStartDate).startOf('day')
                : null;

            const end = r.contractEndDate
                ? DateTime.fromISO(r.contractEndDate).endOf('day')
                : null;

            if (!start || !start.isValid) return false;

            return start <= today && (!end || today <= end);
        });
        return hasActive;
    }

    addAddress(a: Address | undefined = undefined): void {
        const group = createPostalAddressFormGroup(false, true);
        if (a) {
            group.patchValue(a);
        }
        this.addressesFA.push(group);
    }

    deleteAddress(i: number): void {
        console.debug('deleteAddress', i)
        this.addressesFA.removeAt(i);
    }

    addPhoneNumber(value: PhoneNumber | undefined = undefined): void {
        // console.debug('addPhoneNumber', value);
        const group = createPhoneNumberFormGroup(true);
        if (value) {
            group.patchValue(value);
        }
        this.phoneNumbersFA.push(group);
    }

    deletePhoneNumber(i: number): void {
        console.debug('deletePhoneNumber', i)
        this.phoneNumbersFA.removeAt(i);
    }

    getContactSummary(id: any): ContactSummary | null {
        if (!id) {
            return null;
        }
        const contact = this.contacts.value().find(c => c.id === id);
        return contact ?? null;
    }

    getLanguage(id: any): Language | null {
        if (!id) {
            return null;
        }
        const language = this.languages.value().find(l => l.id === id);
        return language ?? null;
    }

    addMembershipRecord(): void {
        console.debug('addMembershipRecord');
        // Add a new membership record with default values, taken from the most recent membership record if it exists
        if (this.membershipRecordsFA.length > 0) {
            const lastRecord = this.membershipRecordsFA.at(this.membershipRecordsFA.length - 1);
            const newRecord: Partial<MembershipRecord> = {
                trialMember: false,
                memberType: lastRecord.controls.memberType.value,
                topics: lastRecord.controls.topics.value,
                contractStartDate: null,
                contractEndDate: null,
                primaryContact: this.getContactSummary(lastRecord.controls.primaryContact.value?.id),
                invoicingContact: this.getContactSummary(lastRecord.controls.invoicingContact.value?.id),
                contractContact: this.getContactSummary(lastRecord.controls.contractContact.value?.id),
                notes: lastRecord.controls.notes.value,
                language: this.getLanguage(lastRecord.controls.language.value?.id),
                engagementLevel: lastRecord.controls.engagementLevel.value
            };
            this.membershipRecordsFA.push(this.createMembershipRecordFormGroup(newRecord));
        } else {
            if (this.pastMembershipRecords.length > 0) {
                const lastRecord = this.pastMembershipRecords.at(this.pastMembershipRecords.length - 1);
                if (lastRecord) {
                    const newRecord: Partial<MembershipRecord> = {
                        trialMember: false,
                        memberType: lastRecord.memberType,
                        topics: lastRecord.topics,
                        contractStartDate: null,
                        contractEndDate: null,
                        primaryContact: this.getContactSummary(lastRecord.primaryContact?.id),
                        invoicingContact: this.getContactSummary(lastRecord.invoicingContact?.id),
                        contractContact: this.getContactSummary(lastRecord.contractContact?.id),
                        notes: lastRecord.notes,
                        language: this.getLanguage(lastRecord.language?.id),
                        engagementLevel: lastRecord.engagementLevel
                    };
                    this.membershipRecordsFA.push(this.createMembershipRecordFormGroup(newRecord));
                } else {
                    this.membershipRecordsFA.push(this.createMembershipRecordFormGroup());
                }
            } else {
                this.membershipRecordsFA.push(this.createMembershipRecordFormGroup());
            }
        }
    }

    deleteMembershipRecord(index: number) {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Please confirm',
                message: 'Are you sure you want to proceed? The membership data will be cleared.'
            }
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.membershipRecordsFA.removeAt(index);
            } else {
                console.debug('no change');
            }
        });
    }

    toggleSupplier(event: MatCheckboxChange): void {
        // UI sets the check state right upon click, so I have to force it back
        // to its current value before asking for confirmation
        event.source.checked = this.isSupplier();
        // Following task in Planner "when you click on "supplier" for a contact or org, 
        // clean up the "stakeholder group" field from any entry previously created"
        if (!this.isSupplier()) {
            const dialogRef = this.dialog.open(ConfirmDialogComponent, {
                data: {
                    title: 'Please confirm',
                    message: 'Creating supplier data will clear stakeholder groups.'
                }
            });
            dialogRef.afterClosed().subscribe(result => {
                if (result) {
                    this.isSupplier.set(true);
                    this.isSupplierControl.setValue(true);
                    this.organisationFG.controls.stakeholderGroups.patchValue([]);
                    this.organisationFG.controls.stakeholderGroups.setValidators([]);
                    this.organisationFG.controls.stakeholderGroups.updateValueAndValidity();
                    this.organisationFG.controls.supplierDetails.enable();
                } else {
                    console.debug('no change');
                }
            });
            return;
        } else {
            const dialogRef = this.dialog.open(ConfirmDialogComponent, {
                data: {
                    title: 'Please confirm',
                    message: 'Are you sure you want to proceed? Supplier data will be cleared.'
                }
            });
            dialogRef.afterClosed().subscribe(result => {
                if (result) {
                    this.isSupplier.set(false);
                    this.isSupplierControl.setValue(false);
                    this.organisationFG.controls.stakeholderGroups.setValidators(Validators.required);
                    this.organisationFG.controls.stakeholderGroups.updateValueAndValidity();
                    this.organisationFG.controls.supplierDetails.disable();
                    this.organisationFG.controls.supplierDetails.reset();
                } else {
                    console.debug('no change');
                }
            });
        }
    }

    onAddInitiative(value: string) {
        console.debug('adding initiative', value, 'to list')
        this.initiativeService.create({ id: 0, name: value, }).subscribe({
            next: (item) => {
                this.initiatives.reload();
                const current = this.organisationFG.get('initiatives')!.value ?? [];
                console.debug('current', current)
                this.organisationFG.controls['initiatives'].setValue([...current, item]);
                console.log('✅ Initiative added', item)
                this._snackBar.open('✅ Initiative added', 'Close', { duration: 3000 });
            },
            error: (err) => {
                console.error('error adding initiative', err);
                this._snackBar.open('❌ Error adding initiative', 'Close', { duration: 3000 });
            }
        })
    }

    onAddLanguage(i: number, value: string) {
        console.debug('adding language', value, 'to list')
        this.languageService.create({ id: 0, name: value, }).subscribe({
            next: (item) => {
                this.languages.reload();
                this.membershipRecordsFA.at(i).controls['language'].setValue({ ...item, icons: [] });
                console.log('✅ Language added', item)
                this._snackBar.open('✅ Language added', 'Close', { duration: 3000 });
            },
            error: (err) => {
                console.error('error adding language', err);
                this._snackBar.open('❌ Error adding language', 'Close', { duration: 3000 });
            }
        })
    }


    cancel() {
        this.router.navigateByUrl(this.returnUrl);
    }

    save() {
        console.debug('save', this.organisationFG.valid);
        this.organisationFG.markAllAsTouched();

        if (this.organisationFG.invalid) {
            console.debug('invalid forms', this.organisationFG);
            this._snackBar.open('❌ Please check for missing or invalid fields', 'Close', { duration: 3000 });
            return
        }

        const todayEnd = DateTime.now().endOf('day');

        const hasPastEndDate = this.membershipRecordsFA.controls.some(c => {
            const ed = normalizeDateTime(c.controls.contractEndDate.value);
            return ed && ed < todayEnd;
        });

        if (hasPastEndDate) {
            const dialogRef = this.dialog.open(ConfirmDialogComponent, {
                data: {
                    title: 'You are creating a past membership record.',
                    message: 'Setting a past end date in a membership record will make it unmodifiable in the future. Do you confirm the set end dates?'
                }
            });
            dialogRef.afterClosed().subscribe(result => {
                if (result) {
                    this.update();
                } else {
                    console.debug('cancelling organisation update')
                    this._snackBar.open('❌ Update canceled ', 'Close', { duration: 3000 });
                    return;
                }
            });
        } else {
            this.update();
        }
    }

    private update() {
        // Have to test memberDataFG with invalid instead of valid because when the form is disabled 
        // it is also marked as invalid, but I want to allow that state

        if (this.organisationFG.valid
            && ((this.isSupplier() && this.supplierDetailsFG.valid) || !this.isSupplier())) {
            console.debug('organisationFG', this.organisationFG.getRawValue())
            console.debug('membershipRecordsFA', this.membershipRecordsFA.getRawValue());
            console.debug('supplierDetailsFG', this.supplierDetailsFG.getRawValue());

            const commonData = this.organisationFG.getRawValue();
            const supplierDetails = this.supplierDetailsFG.getRawValue();

            const updatedOrganisation: Organisation = new Organisation({
                ...this.organisation,
                membershipRecords: this.pastMembershipRecords
            });

            updatedOrganisation.name = commonData.name!;
            updatedOrganisation.acronym = commonData.acronym;
            updatedOrganisation.stakeholderGroups = commonData.stakeholderGroups;
            updatedOrganisation.addresses = commonData.addresses;
            updatedOrganisation.taxId = commonData.taxId;
            updatedOrganisation.phoneNumbers = commonData.phoneNumbers;
            updatedOrganisation.projects = commonData.projects;
            updatedOrganisation.initiatives = commonData.initiatives
            updatedOrganisation.topics = commonData.topics;
            updatedOrganisation.website = commonData.website;
            updatedOrganisation.targetAudiences = commonData.targetAudiences;
            updatedOrganisation.additionalInfo = commonData.additionalInfo;

            // Only consider editable membership records, since past membership 
            // records can't be touched
            this.membershipRecordsFA.controls.forEach(mrFG => {
                const raw = mrFG.getRawValue();
                console.debug('MR', mrFG);
                let isoStartDate = normalizeDate(raw.contractStartDate);
                let isoEndDate = normalizeDate(raw.contractEndDate);
                if (raw.id === null) {
                    const newRecord = new MembershipRecord();
                    newRecord.trialMember = raw.trialMember;
                    newRecord.memberType = raw.memberType;
                    newRecord.engagementLevel = raw.engagementLevel;
                    newRecord.contractStartDate = isoStartDate;
                    newRecord.contractEndDate = isoEndDate;
                    newRecord.notes = raw.notes;
                    newRecord.topics = raw.topics;
                    newRecord.language = raw.language ? this.languages.value().find(l => l.id == raw.language?.id) ?? null : null;
                    newRecord.primaryContact = raw.primaryContact ? this.contacts.value().find(c => c.id == raw.primaryContact?.id) ?? null : null;
                    newRecord.invoicingContact = raw.invoicingContact ? this.contacts.value().find(c => c.id == raw.invoicingContact?.id) ?? null : null;
                    newRecord.contractContact = raw.contractContact ? this.contacts.value().find(c => c.id == raw.contractContact?.id) ?? null : null;
                    updatedOrganisation.membershipRecords.push(newRecord);
                } else {
                    const existingRecord = this.organisation.membershipRecords.find(r => (r.id == raw.id));
                    if (existingRecord) {
                        existingRecord.trialMember = raw.trialMember;
                        existingRecord.memberType = raw.memberType;
                        existingRecord.engagementLevel = raw.engagementLevel;
                        existingRecord.contractStartDate = isoStartDate;
                        existingRecord.contractEndDate = isoEndDate;
                        existingRecord.notes = raw.notes;
                        existingRecord.topics = raw.topics;
                        existingRecord.language = raw.language ? this.languages.value().find(l => l.id == raw.language?.id) ?? null : null;
                        existingRecord.primaryContact = raw.primaryContact ? this.contacts.value().find(c => c.id == raw.primaryContact?.id) ?? null : null;
                        existingRecord.invoicingContact = raw.invoicingContact ? this.contacts.value().find(c => c.id == raw.invoicingContact?.id) ?? null : null;
                        existingRecord.contractContact = raw.contractContact ? this.contacts.value().find(c => c.id == raw.contractContact?.id) ?? null : null;
                        updatedOrganisation.membershipRecords.push(existingRecord);
                    } else {
                        console.error('Membership Record not found', raw.id);
                    }
                }
            })
            // Some membership records may have been removed by the user
            const originalMRIds = this.organisation.membershipRecords.map(r => r.id);
            const updatedMRIds = updatedOrganisation.membershipRecords.map(v => v.id);
            const removedMRIds = originalMRIds.filter(id => !updatedMRIds.includes(id));
            console.debug('removedMRIds', originalMRIds, updatedMRIds, removedMRIds)

            if (this.isSupplier()) {
                updatedOrganisation.supplierDetails = supplierDetails;
            } else {
                updatedOrganisation.supplierDetails = null;
            }

            console.debug('updatedOrganisation', updatedOrganisation);

            if (this.mode === 'Create') {
                this.organisationService.create(updatedOrganisation).subscribe({
                    next: () => {
                        this.router.navigateByUrl(this.returnUrl,
                            {
                                state: { message: '✅ Organisation successfully added' }
                            }
                        );
                    },
                    error: (err) => {
                        console.error('error adding organisation', err)
                        this._snackBar.open('❌ Error adding organisation', 'Close', { duration: 3000 });
                    }
                })
            } else {
                this.organisationService.update(updatedOrganisation).subscribe({
                    next: () => {
                        this.router.navigateByUrl(this.returnUrl,
                            {
                                state: { message: '✅ Organisation successfully updated' }
                            }
                        );
                    },
                    error: (err) => {
                        console.error('error updating organisation', err)
                        this._snackBar.open('❌ Error updating organisation', 'Close', { duration: 3000 });
                    }
                })
            }
        }
    }

    delete() {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Delete organisation',
                message: 'Are you sure you want to delete this organisation? It will also be removed from any contact affiliated with it.'
            }
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result && this.organisation.id) {
                this.organisationService.delete(this.organisation.id).subscribe({
                    next: () => {
                        this.router.navigateByUrl(this.returnUrl,
                            {
                                state: { message: '✅ Organisation deleted' }
                            }
                        );
                    },
                    error: (err) => {
                        console.error('error deleting organisation', err)
                        this._snackBar.open('❌ Error deleting organisation', 'Close', { duration: 3000 });
                    }
                })
            }
        });
    }

    toggleArchive(): void {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: `${this.organisation.archived ? 'Un-archive' : 'Archive'} organisation`,
                message: `Are you sure you want to ${this.organisation.archived ? 'un-archive' : 'archive'} this organisation?`
            }
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result && this.organisation.id) {
                this.organisationService.setArchivedStatus(this.organisation.id, !this.organisation.archived).subscribe({
                    next: () => {
                        this.router.navigateByUrl(this.returnUrl,
                            {
                                state: { message: `✅ Crganisation ${this.organisation.archived ? 'un-archived' : 'archived'}` }
                            }
                        );
                    },
                    error: (err) => {

                        console.error(`error ${this.organisation.archived ? 'un-archiving' : 'archiving'} organisation`, err)
                        this._snackBar.open(`❌ Error ${this.organisation.archived ? 'un-archiving' : 'archiving'} organisation`, 'Close', { duration: 3000 });
                    }
                })
            }
        });
    }
}
